import { keymap } from "prosemirror-keymap";
import type { Plugin, EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { TextSelection } from "prosemirror-state";
import type { ViewspecState } from "../viewspecs/types.ts";
import { nlsSchema } from "./schema.ts";

interface KeymapCallbacks {
  focusCommandBar: () => void;
  setViewspecs: (vs: Partial<ViewspecState>) => void;
  navigate: (path: string) => void;
  executeCommand: (name: string) => void;
}

/**
 * Find the statement node at the current cursor position.
 * Returns { pos, node, index } where pos is the position of the statement
 * in the doc, and index is its child index.
 */
function findCurrentStatement(state: EditorState) {
  const { $from } = state.selection;
  for (let depth = $from.depth; depth >= 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "statement") {
      const pos = $from.before(depth);
      // Find index among doc children
      let index = 0;
      state.doc.forEach((_child, _offset, i) => {
        if (_offset === pos) index = i;
      });
      return { pos, node, index, depth };
    }
  }
  return null;
}

/**
 * Enter: split the current statement at cursor, creating a new statement
 * with the same level. Text after cursor goes to the new statement.
 */
function handleEnter(state: EditorState, dispatch?: (tr: any) => void) {
  const { $from, $to } = state.selection;

  // Find the statement we're in
  let stmtDepth = -1;
  for (let d = $from.depth; d >= 0; d--) {
    if ($from.node(d).type.name === "statement") {
      stmtDepth = d;
      break;
    }
  }
  if (stmtDepth < 0) return false;

  const stmtNode = $from.node(stmtDepth);
  const level = stmtNode.attrs.level as number;

  if (dispatch) {
    // Position in the statement's content
    const startOfContent = $from.start(stmtDepth);
    const endOfContent = $from.end(stmtDepth);

    // Get content after cursor (will go into new statement)
    const afterSlice = state.doc.slice($from.pos, endOfContent);

    // Create new statement node with same level, containing the after-content
    const newStmtContent = afterSlice.content;
    const newStmt = nlsSchema.nodes.statement!.create(
      { sid: "", level, collapsed: false },
      newStmtContent
    );

    // Build transaction:
    // 1. Delete from cursor to end of current statement content
    // 2. Insert new statement after current one
    const stmtEnd = $from.after(stmtDepth);
    let tr = state.tr;

    // Delete content after cursor in current statement
    if ($from.pos < endOfContent) {
      tr = tr.delete($from.pos, endOfContent);
    }

    // Insert new statement after current statement
    // After the delete, the statement end position may have shifted
    const insertPos = tr.mapping.map(stmtEnd);
    tr = tr.insert(insertPos, newStmt);

    // Set cursor to start of new statement
    const newStmtStart = insertPos + 1; // +1 for the statement node opening
    tr = tr.setSelection(TextSelection.create(tr.doc, newStmtStart));

    dispatch(tr.scrollIntoView());
  }

  return true;
}

/**
 * Tab: indent the current statement (increase level by 1).
 * Only allowed if the previous sibling exists (can't indent the first child).
 * Always returns true to prevent browser default Tab (focus change) behavior.
 */
export function handleTab(state: EditorState, dispatch?: (tr: any) => void) {
  const info = findCurrentStatement(state);
  if (!info) return true; // still consume Tab to prevent focus loss

  const { pos, node } = info;
  const currentLevel = node.attrs.level as number;

  // Find the previous statement to check if indenting is valid
  let prevLevel = -1;
  state.doc.forEach((child, offset) => {
    if (offset < pos && child.type.name === "statement") {
      prevLevel = child.attrs.level as number;
    }
  });

  // Can't indent the very first statement
  if (prevLevel < 0) return true;

  // Can't indent more than one level deeper than previous statement
  if (currentLevel > prevLevel) return true;

  if (dispatch) {
    const tr = state.tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      level: currentLevel + 1,
    });
    dispatch(tr);
  }

  return true;
}

/**
 * Shift-Tab: outdent the current statement (decrease level by 1).
 * Only allowed if level > 1.
 * Always returns true to prevent browser default Shift-Tab behavior.
 */
export function handleShiftTab(state: EditorState, dispatch?: (tr: any) => void) {
  const info = findCurrentStatement(state);
  if (!info) return true; // still consume to prevent focus loss

  const { pos, node } = info;
  const currentLevel = node.attrs.level as number;

  // Can't outdent past level 1
  if (currentLevel <= 1) return true;

  if (dispatch) {
    const tr = state.tr.setNodeMarkup(pos, undefined, {
      ...node.attrs,
      level: currentLevel - 1,
    });
    dispatch(tr);
  }

  return true;
}

/**
 * Create the NLS keymap plugin.
 */
export function nlsKeymap(callbacks: KeymapCallbacks): Plugin {
  return keymap({
    // Statement editing
    Enter: handleEnter,

    // Command bar
    "/": () => {
      callbacks.focusCommandBar();
      return true;
    },

    // Quick depth levels (Ctrl-Shift-number)
    "Ctrl-Shift-1": () => {
      callbacks.setViewspecs({ depthLimit: 1 });
      return true;
    },
    "Ctrl-Shift-2": () => {
      callbacks.setViewspecs({ depthLimit: 2 });
      return true;
    },
    "Ctrl-Shift-3": () => {
      callbacks.setViewspecs({ depthLimit: 3 });
      return true;
    },
    "Ctrl-Shift-0": () => {
      callbacks.setViewspecs({ depthLimit: null });
      return true;
    },

    // Navigation
    "Ctrl-j": () => {
      callbacks.executeCommand("jump");
      return true;
    },
    "Ctrl-g": () => {
      callbacks.executeCommand("goto");
      return true;
    },
    "Mod-f": () => {
      callbacks.executeCommand("filter");
      return true;
    },
    "Alt-ArrowLeft": () => {
      history.back();
      return true;
    },
    "Alt-ArrowRight": () => {
      history.forward();
      return true;
    },

    // Split
    "Ctrl-\\": () => {
      callbacks.executeCommand("split");
      return true;
    },

    // Zoom
    "Ctrl-]": () => {
      callbacks.executeCommand("zoom in");
      return true;
    },
    "Ctrl-[": () => {
      callbacks.executeCommand("zoom out");
      return true;
    },
  });
}
